/* ============================================================================
   OVD HEADER COLLAPSE  —  Oak Valley Designs / Library Light Switch
   Companion to assets/ovd-header.css. Read that file first; it carries the
   reasoning for the whole mechanic.
   ----------------------------------------------------------------------------
   WHAT THIS FILE IS RESPONSIBLE FOR

   1. State class. Toggles `ovd-header-collapsed` on <html> so the compact bar
      can appear. Uses an IntersectionObserver on .header__wrapper, which is a
      NON-sticky child of the sticky section — observing the sticky section
      itself would report "intersecting" forever. No sentinel element, no scroll
      listener, no scrollY arithmetic, no hard-coded pixel thresholds: the
      trigger point is wherever the logo row actually leaves the visible band,
      whatever the announcement bar, the logo size or the font stack do to it.

   2. The compact bar. Built here rather than in Liquid so that the Liquid diff
      stays a two-line, portable patch that can be moved onto the live theme at
      switch-over without touching sections/header.liquid.

   3. --announcement-bar-height reactivity, so the collapse point tracks a
      changing announcement bar instead of a value measured once at parse time.

   4. <safe-sticky> correction. See section F.

   5. Focus hold, so the desktop search box cannot scroll itself off-screen
      while someone is typing in it.

   6. The arming flag, --ovd-armed. The CSS performs the collapse but cannot
      see whether the things that make a collapsed header usable are actually
      there. This file publishes that verdict, and the collapse is multiplied by
      it, so a missing script, a throw, or a header layout with no nav strip
      renders stock Focal rather than a header with no cart and no search.

   HOUSE RULES THIS FILE OBEYS
   - Never writes --header-height or --header-height-without-bottom-nav.
     theme.js:4708-4719 owns both through a ResizeObserver and would win.
   - Never touches window.fetch or XMLHttpRequest. Six installed apps
     (HulkApps, PageFly, Globo, EZ Terms, Klaviyo, Gorgias) monkey-patch both
     on this store; anything relying on either is already known-broken here.
   - State classes go on documentElement, never on <store-header>. The theme
     editor replaces the entire header section on shopify:section:load, which
     would silently drop a class parked on the section's own markup.
   - Everything is wrapped so a throw cannot take the header down with it. A
     dead compact bar is a cosmetic regression; a thrown error during header
     init on a live storefront is not.
   ========================================================================= */
(function () {
  'use strict';

  var root = document.documentElement,
      HEADER_ID = 'shopify-section-header',
      ANNOUNCEMENT_ID = 'shopify-section-announcement-bar',
      CLASS_COLLAPSED = 'ovd-header-collapsed',
      CLASS_HOLD = 'ovd-header-hold',
      CLASS_AWAY = 'ovd-header-away';

  /* STYLESHEET PRESENCE PROBE. The compact bar is styled entirely by
     assets/ovd-header.css — without it the bar is a static, in-flow block, so a
     second copy of the logo, the search icon and a live cart count would render
     under the real header on every template at every width, growing the section
     and desyncing everything anchored to its height. --ovd-collapse-enabled is
     declared on bare :root in that file, so it resolves to "0" at any width
     whenever the file loaded and to the empty string only when it did not.
     This script is deferred and the <link> precedes it in <head>, and a script
     cannot execute while a stylesheet that blocks scripts is still pending, so
     an empty value here means genuinely absent rather than not-yet-applied.
     Costs one computed-style read; buys "no CSS, no markup". */
  if (getComputedStyle(root).getPropertyValue('--ovd-collapse-enabled').trim() === '') {
    return;
  }

  /* Live handles, torn down and rebuilt whenever the theme editor re-renders
     the header section. */
  var intersectionObserver = null,
      observedWrapper = null,
      observedRootMargin = null,
      lastIsIntersecting = true,
      ariaObservers = [],
      announcementObserver = null,
      safeStickyScheduled = false;

  /* --- small helpers ----------------------------------------------------- */

  function guard(fn) {
    return function () {
      try {
        return fn.apply(this, arguments);
      } catch (error) {
        /* Never let a header enhancement break the page. */
        if (window.console && window.console.warn) {
          window.console.warn('[ovd-header]', error);
        }
      }
    };
  }

  function cssNumber(name) {
    var value = parseFloat(getComputedStyle(root).getPropertyValue(name));
    return isNaN(value) ? 0 : value;
  }

  /* THE ARMING FLAG. See the --ovd-armed comment in ovd-header.css for why the
     collapse must be opt-in from here rather than opt-out. Written as an inline
     custom property on documentElement so a single declaration governs both the
     CSS mechanic and every calculation below it. Removing it falls back to the
     stylesheet's 0. */
  function arm() {
    root.style.setProperty('--ovd-armed', '1');
  }

  function disarm() {
    root.style.removeProperty('--ovd-armed');
  }

  /* True only where the collapse is actually in force: armed, at >=1200px (the
     only band where .header__bottom-navigation is displayed, so the only band
     where there is a nav strip to collapse to), and with the sticky header
     setting on. Read live rather than cached — one media query lives in the CSS
     and nowhere else, so CSS and JS can never disagree about the breakpoint. */
  function collapseEnabled() {
    return cssNumber('--ovd-armed') > 0 &&
           cssNumber('--ovd-collapse-enabled') > 0 &&
           cssNumber('--enable-sticky-header') > 0;
  }

  /* Must stay term-for-term identical to --ovd-collapse-distance in the CSS. */
  function collapseDistance() {
    return Math.round(
      cssNumber('--header-height-without-bottom-nav') *
      cssNumber('--enable-sticky-header') *
      cssNumber('--ovd-collapse-enabled') *
      cssNumber('--ovd-armed')
    );
  }

  /* The top edge of the IntersectionObserver's root, as a positive inset.
     Deliberately NOT a height: it is one pixel PAST the announcement bar's
     bottom edge, and the ceil is likewise a bias rather than a rounding.

     The reason is that the collapse subtracts exactly the logo row's own height
     from the header's sticky top, so once the header locks, the logo row's
     bottom edge comes to rest at exactly the announcement bar's bottom edge —
     permanently, not in passing. An observation root placed on that same line
     is edge-adjacent with the row it is watching, and the IntersectionObserver
     spec counts edge-adjacent rects as intersecting even at zero area. Landing
     on the line therefore reports "still visible" forever and the collapsed
     state never applies. A fractional announcement bar (its height is measured
     from borderBoxSize, so 48.4px is ordinary) rounds the other way into a real
     overlap and fails identically.

     One pixel of clearance removes the whole class of problem: the boundary is
     strictly below the resting edge for every announcement-bar height, sticky
     or not. It costs one pixel of scroll in trigger precision. */
  function observationBoundary() {
    return Math.ceil(
      cssNumber('--announcement-bar-height') * cssNumber('--enable-sticky-announcement-bar')
    ) + 1;
  }

  function headerSection() {
    return document.getElementById(HEADER_ID);
  }

  /* ------------------------------------------------------------------------
     A. STATE CLASS
     The observed element is .header__wrapper (the logo row). It is an ordinary
     in-flow child of the sticky section, so it travels with the section and
     slides out of view exactly when the collapse completes.

     rootMargin shrinks the observation root down past the sticky announcement
     bar. Without it the wrapper would still count as "intersecting" while it
     sits behind the announcement bar (the observer knows nothing about
     overlapping fixed chrome), and the class would never be applied on a store
     with a sticky announcement bar — which is exactly OVD's current setting.

     Below 1200px this is inert without any breakpoint logic of its own: the CSS
     applies no negative offset there, so the wrapper stays pinned inside the
     visible band and never stops intersecting. collapseEnabled() is belt and
     braces on top of that, and is what guarantees the class cannot be applied
     if the sticky-header setting is ever switched off (in which case the header
     scrolls away completely and the wrapper does leave the viewport).
     --------------------------------------------------------------------- */

  function applyState() {
    var collapsed = collapseEnabled() && !lastIsIntersecting;
    root.classList.toggle(CLASS_COLLAPSED, collapsed);
  }

  /* Returns whether an observer is live on the current logo row, so init can
     refuse to arm the collapse when there is nothing watching for it. */
  function observeWrapper() {
    var section = headerSection(),
        wrapper = section && section.querySelector('.header__wrapper');

    if (!wrapper || !window.IntersectionObserver) {
      return false;
    }

    var rootMargin = '-' + observationBoundary() + 'px 0px 0px 0px';

    if (intersectionObserver && wrapper === observedWrapper && rootMargin === observedRootMargin) {
      return true;
    }

    if (intersectionObserver) {
      intersectionObserver.disconnect();
    }

    observedWrapper = wrapper;
    observedRootMargin = rootMargin;

    intersectionObserver = new IntersectionObserver(guard(function (entries) {
      lastIsIntersecting = entries[entries.length - 1].isIntersecting;
      applyState();
    }), { threshold: 0, rootMargin: rootMargin });

    intersectionObserver.observe(wrapper);
    return true;
  }

  /* ------------------------------------------------------------------------
     B. --announcement-bar-height REACTIVITY
     sections/announcement-bar.liquid:132 measures the section exactly once, at
     parse time. theme.js:2823-2829 re-measures, but observes the inner
     <announcement-bar> custom element rather than the section. This observer
     watches the section — the box that actually pins at the top of the viewport
     and therefore the box the header's offset has to clear.

     It is created after theme.js's, and ResizeObserver callbacks run in
     observer-creation order, so within a single delivery this value is the one
     that lands. It also feeds the IntersectionObserver's rootMargin, which is
     why the collapse point follows a message that wraps to two lines instead of
     drifting.
     --------------------------------------------------------------------- */

  function measure(entry) {
    if (entry.borderBoxSize) {
      var box = entry.borderBoxSize.length > 0 ? entry.borderBoxSize[0] : entry.borderBoxSize;
      if (box && typeof box.blockSize === 'number') {
        return box.blockSize;
      }
    }
    return entry.target.clientHeight;
  }

  function observeAnnouncementBar() {
    var section = document.getElementById(ANNOUNCEMENT_ID);

    if (announcementObserver) {
      announcementObserver.disconnect();
      announcementObserver = null;
    }

    if (!section || !window.ResizeObserver) {
      return;
    }

    announcementObserver = new ResizeObserver(guard(function (entries) {
      entries.forEach(function (entry) {
        root.style.setProperty('--announcement-bar-height', measure(entry) + 'px');
      });
      /* The pinned band moved, so both the observation root and the
         <safe-sticky> correction have to be re-derived. */
      observeWrapper();
      scheduleSafeSticky();
    }));

    announcementObserver.observe(section);
  }

  /* ------------------------------------------------------------------------
     C. THE COMPACT BAR
     Layout: [mini logo -> /]  [the store's own centred nav, untouched]
             [search] [cart with a live count]

     The centre is deliberately empty. The bar is a transparent overlay with
     pointer-events:none, so the real navigation shows through it and keeps
     every hover target it has today, including the invisible ::before box that
     holds mega menus open.

     IT IS A POINTER AFFORDANCE ONLY: aria-hidden on the container, tabindex=-1
     on every control. The collapse moves the header, it does not hide it — the
     real Cart, Search and logo in the logo row are merely off-screen, and
     off-screen content is still in the accessibility tree and still in the tab
     order. Left focusable, this bar would put a second identically-labelled
     Cart, a second cart count and a second Search in front of every screen
     reader user whenever the header is collapsed, and would give keyboard users
     a tab stop that vanishes under them the moment they scroll back to the top
     (a control hidden while focused drops focus to <body>). Excluding it costs
     assistive-technology users nothing: the real controls stay reachable, and
     the focus hold in section D pins them back on screen when they are.
     --------------------------------------------------------------------- */

  /* Customized built-ins (`is="toggle-link"`) are not supported everywhere, and
     may not upgrade at all when cloned. So the compact controls are plain
     anchors that PROXY their click to the real element, and only when that real
     element is genuinely an upgraded ToggleLink pointing at an element that
     exists on this page. Everywhere else the anchor's own href is left to do
     its job, which is the same fallback the stock header already relies on. */
  function isUpgradedToggleLink(element) {
    try {
      var Ctor = window.customElements && window.customElements.get('toggle-link');
      return !!(Ctor && element instanceof Ctor);
    } catch (error) {
      return false;
    }
  }

  function proxyControl(compact, real) {
    if (!real) {
      return;
    }

    var controls = real.getAttribute('aria-controls');

    if (controls) {
      compact.setAttribute('aria-controls', controls);
      compact.setAttribute('aria-expanded', real.getAttribute('aria-expanded') || 'false');
    }

    compact.addEventListener('click', guard(function (event) {
      /* Let cmd/ctrl/shift/middle clicks open the href the way the user asked. */
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
        return;
      }
      if (!isUpgradedToggleLink(real) || !real.controlledElement) {
        return; /* no drawer available: fall through to plain navigation */
      }
      event.preventDefault();
      real.click();
    }));

    /* The drawer can also be closed from inside itself, or opened by something
       else entirely, so mirror the real control's state rather than tracking
       our own. */
    if (controls && window.MutationObserver) {
      var observer = new MutationObserver(guard(function () {
        compact.setAttribute('aria-expanded', real.getAttribute('aria-expanded') || 'false');
      }));
      observer.observe(real, { attributes: true, attributeFilter: ['aria-expanded'] });
      ariaObservers.push(observer);
    }
  }

  function buildSearch(section) {
    /* With header_layout = logo_center_search_open the desktop search is the
       open input in the logo row, and the drawer toggle that would normally sit
       in the icon list is display:none at >=1200px. It is still in the DOM and
       still an upgraded ToggleLink, and HTMLElement.click() dispatches on a
       hidden element, so proxying to it opens the same predictive-search drawer
       the mobile header uses. */
    var real = section.querySelector('a[is="toggle-link"][aria-controls="search-drawer"]'),
        link = document.createElement('a');

    link.className = 'ovd-compact-bar__action ovd-compact-bar__search';
    link.setAttribute('href', (real && real.getAttribute('href')) || '/search');
    link.setAttribute('aria-label', (real && real.getAttribute('aria-label')) || 'Search');
    link.setAttribute('tabindex', '-1');

    var icon = (real && real.querySelector('svg')) ||
               section.querySelector('.header__search-bar svg');

    if (icon) {
      var clone = icon.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('focusable', 'false');
      link.appendChild(clone);
    }

    proxyControl(link, real);
    return link;
  }

  /* Account. Only rendered when the shop has customer accounts enabled, so
     this returns null and the caller appends nothing when it does not exist. */
  function buildAccount(section) {
    /* Located via its icon and walked up, the same way buildCart finds the cart
       through <cart-count>. A selector anchored on .header__secondary-links
       misses it: the icon wrappers are not inside that element in this layout,
       and the href is an absolute shopify.com account URL under new customer
       accounts, so matching on the path is unreliable too. */
    var icon = section.querySelector('svg.icon--header-customer'),
        real = icon ? icon.closest('a') : null,
        link;

    if (!real) {
      return null;
    }

    link = document.createElement('a');
    link.className = 'ovd-compact-bar__action ovd-compact-bar__account';
    link.setAttribute('href', real.getAttribute('href') || '/account');
    link.setAttribute('aria-label', real.getAttribute('aria-label') || 'Account');
    link.setAttribute('tabindex', '-1');

    var clone = icon.cloneNode(true);

    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('focusable', 'false');
    link.appendChild(clone);

    /* A plain link, not a drawer toggle, so there is nothing to proxy. */
    return link;
  }

  function buildCart(section) {
    /* Found via <cart-count> rather than a selector tied to one header layout:
       the icon variant and the text-linklist variant both contain one, and only
       the icon variant carries aria-controls when the cart type is a drawer. */
    var realCount = section.querySelector('.header__secondary-links cart-count'),
        real = realCount ? realCount.closest('a') : null,
        link = document.createElement('a');

    link.className = 'ovd-compact-bar__action ovd-compact-bar__cart';
    /* href stays a real /cart URL so the control works with JS off, with the
       drawer disabled, and on the cart page itself where the theme removes the
       toggle behaviour. */
    link.setAttribute('href', (real && real.getAttribute('href')) || '/cart');
    link.setAttribute('aria-label', (real && real.getAttribute('aria-label')) || 'Cart');
    link.setAttribute('data-no-instant', '');
    link.setAttribute('tabindex', '-1');

    var icon = real && real.querySelector('svg');

    if (icon) {
      var iconClone = icon.cloneNode(true);
      iconClone.setAttribute('aria-hidden', 'true');
      iconClone.setAttribute('focusable', 'false');
      link.appendChild(iconClone);
    }

    if (realCount) {
      /* <cart-count> is an autonomous custom element, so cloneNode(true)
         produces an upgraded instance; its connectedCallback subscribes to
         cart:updated and cart:refresh on documentElement, which is where the
         theme dispatches them. The clone therefore stays live on its own with
         no wiring from us. */
      var countClone = realCount.cloneNode(true);
      countClone.className = 'header__cart-count header__cart-count--floating bubble-count';
      link.appendChild(countClone);
    }

    proxyControl(link, real);
    return link;
  }

  function buildCompactBar(section) {
    /* Idempotent: a second call must not produce a second bar. */
    if (section.querySelector('.ovd-compact-bar')) {
      return;
    }

    var bar = document.createElement('div'),
        inner = document.createElement('div'),
        actions = document.createElement('div');

    bar.className = 'ovd-compact-bar';
    /* Visual duplicate of controls that are still present, still labelled and
       still reachable in the logo row. See section C's header. */
    bar.setAttribute('aria-hidden', 'true');
    inner.className = 'ovd-compact-bar__inner';
    actions.className = 'ovd-compact-bar__actions';

    /* Search sits far left and cart far right, mirroring where both already
       are in the expanded header, so the collapsed row reads as the same
       header rather than a different one. */
    var lead = document.createElement('div');
    lead.className = 'ovd-compact-bar__lead';
    lead.appendChild(buildSearch(section));

    var account = buildAccount(section);

    if (account) {
      actions.appendChild(account);
    }

    actions.appendChild(buildCart(section));

    inner.appendChild(lead);
    inner.appendChild(actions);
    bar.appendChild(inner);

    /* Appended to the section, not to <store-header>, so the section (already
       position:sticky) is the containing block and nothing new has to be
       positioned. */
    section.appendChild(bar);
  }

  /* ------------------------------------------------------------------------
     D. FOCUS HOLD
     A real bug, not a nicety. The desktop search input lives in the logo row.
     Collapsed, that row is off-screen but still in the tab order, so a keyboard
     user can tab into a search box they cannot see, and a mouse user who has
     scrolled while the predictive results are open loses the input they were
     typing into. Holding the header fully pinned while focus is anywhere in the
     logo row — or in the predictive-search drawer it opens — fixes both. The
     compact bar needs no protection of its own here: it is aria-hidden with
     tabindex=-1 throughout, so focus can never be inside it when the CSS hides
     it for the duration of a hold.
     --------------------------------------------------------------------- */

  function isHeldTarget(node) {
    if (!node) {
      return false;
    }
    var section = headerSection(),
        wrapper = section && section.querySelector('.header__wrapper'),
        drawer = document.getElementById('search-drawer');

    return !!((wrapper && wrapper.contains(node)) || (drawer && drawer.contains(node)));
  }

  function refreshHold() {
    var held = collapseEnabled() && isHeldTarget(document.activeElement);
    root.classList.toggle(CLASS_HOLD, held);
  }

  /* Delegated on the document so it survives every header re-render without
     re-binding, and so it also covers the search drawer, which is appended to
     <body> rather than left inside the header section. */
  document.addEventListener('focusin', guard(refreshHold));
  document.addEventListener('focusout', guard(function () {
    /* activeElement is <body> during focusout; wait a tick for the new one. */
    window.requestAnimationFrame(guard(refreshHold));
  }));

  /* ------------------------------------------------------------------------
     E. <safe-sticky> CORRECTION
     getStickyHeaderOffset() (theme.js:1458) returns

         --header-height * Sh  +  --announcement-bar-height * Sa

     After the collapse the band that actually stays pinned is

        (--header-height - --header-height-without-bottom-nav) * Sh
                              +  --announcement-bar-height * Sa

     The announcement-bar terms cancel identically, so the whole correction is
     exactly  -(--header-height-without-bottom-nav * Sh)  — the same quantity
     the CSS subtracts, read from the same variable. That is the point: this
     file never reimplements getStickyHeaderOffset(), never reads
     --header-height, and never needs to know whether the announcement bar is
     sticky. If --header-height-without-bottom-nav ever disappears, both the CSS
     collapse and this correction go to zero together; the feature switches
     itself off as one unit and there is no state where the header collapses but
     the sticky asides do not follow.

     WHY THE offset ATTRIBUTE. SafeSticky.initialTopOffset (theme.js:1471-1473)
     is a getter with no memo, dereferenced twice per scroll frame (at
     theme.js:1480 and :1486), and it reads the offset attribute off the live
     DOM each time. Writing a negative offset therefore corrects the theme's
     over-estimate without rewriting --header-height (a ResizeObserver owns it,
     and five CSS rules legitimately need its true value), without !important,
     and without replacing the custom element — which would cost the
     tall-sidebar bottom-parking behaviour that keeps a long cart summary's
     checkout button reachable.

     WHERE IT LANDS. sections/main-cart.liquid:322 (the order-summary column,
     unconditional, the store's highest-value desktop page) and
     sections/main-collection.liquid:95 (the filter rail, live only on
     templates/collection.sale.json, the one collection template with
     show_filters:true). main-search.liquid's copy never renders —
     templates/search.json has show_filters:false.

     FAILURE MODES, all benign. If this file 404s, theme.js governs from the
     first scroll frame and the asides sit exactly where they sit today: a gap,
     never a jump. (The CSS `top` is unobservable before the element is stuck,
     and it cannot be stuck before the first scroll frame.) If a future Focal
     caches initialTopOffset at connectedCallback, our write is ignored and the
     outcome is the same. Every degradation path lands on stock Focal.

     The correction is deliberately NOT suspended during .ovd-header-hold.
     initialTopOffset is only a ceiling in SafeSticky's clamp, so a value that
     drops is applied on the very next frame while a value that rises only
     recovers by scrolling upward. Toggling it would make the asides drift
     asymmetrically every time someone clicks into the search box; a constant
     correction is strictly better behaved.
     --------------------------------------------------------------------- */

  function applySafeStickyOffsets() {
    var collapse = collapseDistance(),
        nodes = document.getElementsByTagName('safe-sticky'),
        index, element, base, next;

    for (index = 0; index < nodes.length; index++) {
      element = nodes[index];

      /* Stash whatever Liquid authored, once, so an edit to the section file is
         picked up automatically and a hand-edited attribute is corrected on the
         next frame rather than fought over. */
      if (typeof element.dataset.ovdBaseOffset === 'undefined') {
        element.dataset.ovdBaseOffset = element.getAttribute('offset') || '0';
      }

      base = parseInt(element.dataset.ovdBaseOffset, 10) || 0;
      next = String(base - collapse);

      if (element.getAttribute('offset') !== next) {
        element.setAttribute('offset', next);
      }
    }
  }

  function scheduleSafeSticky() {
    if (safeStickyScheduled) {
      return;
    }
    safeStickyScheduled = true;
    window.requestAnimationFrame(guard(function () {
      safeStickyScheduled = false;
      applySafeStickyOffsets();
    }));
  }

  /* A stale offset is only ever observable while scrolling, and SafeSticky
     re-reads it every frame, so one passive rAF-coalesced scroll listener
     covers every cause of staleness in a single mechanism: web-font reflow,
     cart-count width change, breakpoint crossing, facet AJAX re-render, theme
     editor reload, a new <safe-sticky> appearing. Cheaper per frame than what
     SafeSticky already spends on the same event. */
  window.addEventListener('scroll', guard(scheduleSafeSticky), { passive: true });

  /* ------------------------------------------------------------------------
     F. INIT / RE-INIT
     --------------------------------------------------------------------- */

  var initHeader = guard(function () {
    var section = headerSection();
    if (!section) {
      return;
    }

    /* Any observers pointed at the previous render are dead weight. */
    ariaObservers.forEach(function (observer) { observer.disconnect(); });
    ariaObservers = [];

    /* Disarm first, so a throw anywhere below leaves the storefront on stock
       Focal rather than half-collapsed. guard() swallows the error and abandons
       the rest of this function, and the CSS reads whatever the flag says at
       that moment. */
    disarm();

    buildCompactBar(section);

    /* Arm only when every precondition the CSS cannot check for itself holds:
       a nav strip to collapse TO, a compact bar carrying the controls the
       collapse takes off screen, and an observer to switch that bar on. */
    if (section.querySelector('.header__bottom-navigation') &&
        section.querySelector('.ovd-compact-bar') &&
        observeWrapper()) {
      arm();
    }

    applyState();   /* a re-render that cannot be armed must drop the class */
    refreshHold();
    scheduleSafeSticky();
  });

  var onResize = guard(function () {
    observeWrapper();   /* rootMargin may have changed with the breakpoint */
    applyState();       /* crossing 1200px must clear the class immediately */
    scheduleSafeSticky();
  });

  window.addEventListener('resize', onResize, { passive: true });

  /* The theme editor replaces whole sections. Rebuild for ours, and re-derive
     for the announcement bar, whose height sets both the header's own offset
     and the observation root. */
  document.addEventListener('shopify:section:load', guard(function (event) {
    var target = event.target;
    if (!target || !target.id) {
      return;
    }
    if (target.id === HEADER_ID) {
      if (intersectionObserver) {
        intersectionObserver.disconnect();
      }
      intersectionObserver = null;
      observedWrapper = null;
      observedRootMargin = null;
      initHeader();
    } else if (target.id === ANNOUNCEMENT_ID) {
      observeAnnouncementBar();
      observeWrapper();
    }
    scheduleSafeSticky();
  }));

  document.addEventListener('shopify:section:unload', guard(function (event) {
    if (event.target && event.target.id === HEADER_ID) {
      if (intersectionObserver) {
        intersectionObserver.disconnect();
      }
      intersectionObserver = null;
      observedWrapper = null;
      observedRootMargin = null;
      root.classList.remove(CLASS_COLLAPSED, CLASS_HOLD, CLASS_AWAY);
      /* The header this feature was arming for is gone; the sticky asides have
         to come back off their correction in the same frame. */
      disarm();
      scheduleSafeSticky();
    }
  }));

  /* This script is deferred and placed after theme.js, so by now every custom
     element is defined, <store-header> has connected, and both header height
     variables have been published by its ResizeObserver. */

  /* ------------------------------------------------------------------------
     B. BELOW 1200px - SLIDE THE WHOLE HEADER AWAY ON SCROLL DOWN

     Above 1200px the header collapses onto its nav strip, because there is a
     nav strip to land on. Below 1200px `.header__bottom-navigation` is not
     rendered at all (header.liquid:333 gates it on the logo_center_search_open
     layout, and theme.css hides it under .hidden-pocket/.hidden-lap), so there
     is nothing to collapse TO. Collapsing the logo row there would take the
     burger, the search and the cart off the page with it.

     So below 1200px the whole header slides out of the way while the reader is
     moving down the page, and comes back on any upward scroll. Everything stays
     one flick away, and 93-137px of permanently-pinned chrome becomes 0.

     Same mechanic as the desktop collapse: we move the sticky `top`, we never
     animate height and we never leave flow, so document height is constant and
     Safari has nothing to jump. Deliberately NOT `transform: translateY()` -
     a transform on this element would create a containing block for any
     position:fixed descendant and a new stacking context, which would change
     how the drawers and the mega menu resolve.
     --------------------------------------------------------------------- */

  var awayTicking = false,
      awayLastY = window.pageYOffset || 0,
      awayDelta = 0;

  var JITTER = 6,      /* ignore sub-6px noise: touch wobble and momentum drift */
      HIDE_AT = 8,     /* reluctant to hide  */
      SHOW_AT = 4;     /* eager to reveal    */

  function reducedMotion() {
    return window.matchMedia &&
           window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* Only below 1200px, only once the feature armed, and never when the user has
     asked for less motion - there the header simply stays put, which is the
     honest reading of the preference since the movement IS the feature. */
  function awayEnabled() {
    return cssNumber('--ovd-armed') === 1 &&
           cssNumber('--ovd-collapse-enabled') === 0 &&
           window.matchMedia('(max-width: 1199px)').matches &&
           !reducedMotion();
  }

  /* A drawer, the mobile menu or the search panel is open. Focal scroll-locks
     <html> for these, so the page cannot move anyway - but a stale away-state
     would leave the header off-screen behind the overlay when it closes. */
  function scrollLocked() {
    return root.classList.contains('lock-all') ||
           root.classList.contains('lock-mobile');
  }

  function awayThreshold() {
    return cssNumber('--header-height') +
           cssNumber('--announcement-bar-height') + 120;
  }

  function setAway(next) {
    if (root.classList.contains(CLASS_AWAY) !== next) {
      root.classList.toggle(CLASS_AWAY, next);
    }
  }

  function onAwayScroll() {
    var y = window.pageYOffset || 0,
        step = y - awayLastY;

    awayLastY = y;

    if (!awayEnabled() || scrollLocked() || root.classList.contains(CLASS_HOLD)) {
      awayDelta = 0;
      setAway(false);
      return;
    }

    /* Near the top the header belongs on screen regardless of direction. */
    if (y <= awayThreshold()) {
      awayDelta = 0;
      setAway(false);
      return;
    }

    if (Math.abs(step) < JITTER) {
      return;
    }

    /* Reset the accumulator on a direction change so a long scroll down does
       not have to be "paid back" before an upward flick reveals the header. */
    if ((step > 0) !== (awayDelta > 0)) {
      awayDelta = 0;
    }
    awayDelta += step;

    if (awayDelta >= HIDE_AT) {
      setAway(true);
    } else if (awayDelta <= -SHOW_AT) {
      setAway(false);
    }
  }

  window.addEventListener('scroll', guard(function () {
    if (awayTicking) {
      return;
    }
    awayTicking = true;
    window.requestAnimationFrame(guard(function () {
      awayTicking = false;
      onAwayScroll();
    }));
  }), { passive: true });

  /* Crossing the 1200px boundary, opening a drawer, or moving focus into the
     header must all resolve to a visible header rather than a stale hidden one. */
  window.addEventListener('resize', guard(function () {
    if (!awayEnabled()) {
      awayDelta = 0;
      setAway(false);
    }
  }), { passive: true });

  new MutationObserver(guard(function () {
    if (scrollLocked() || root.classList.contains(CLASS_HOLD)) {
      awayDelta = 0;
      setAway(false);
    }
  })).observe(root, { attributes: true, attributeFilter: ['class'] });

  guard(observeAnnouncementBar)();
  initHeader();
})();
